import type { GetSentimentDataQuery } from '@revelio/data-access';

import { normalizeBarScoresData } from '../../../../sentiment/sentimentV2/hooks/use-get-sentiment-reviews-data';
import { transformToSingleEntity } from '../../../../sentiment/sentimentV2/utils/transform-to-single-entity';
import type { TreeSelection } from '../../../filters/types/filters';
import type { SentimentChartType } from '../../PageConfigProvider/types/chart';
import type {
  SentimentMetricKeys,
  SentimentMetricValues,
} from '../../PageConfigProvider/types/metric';
import type { SentimentData } from '../SentimentDataProvider';
import {
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataSet,
  type TimeseriesDataSet,
} from '../types';

type SentimentEntity = NonNullable<
  NonNullable<GetSentimentDataQuery['sentiment']>[number]
>;

const toTopBottomBreakdown = (
  entity: SentimentEntity
): BreakdownSingleDataSet['data'][number]['breakdown'] => {
  const normalizedData = normalizeBarScoresData({
    sentiment: [entity],
  } as GetSentimentDataQuery);
  const singleEntityData = transformToSingleEntity(normalizedData);

  return singleEntityData.data.map((item) => ({
    dim: item.label,
    value: item.value,
    share: item.value,
    count: item.count ?? undefined,
  }));
};

export const convertSentimentData = ({
  data,
  chartType,
  metric,
  primaryFilterSelections,
}: {
  data: NonNullable<GetSentimentDataQuery['sentiment']>;
  chartType: SentimentChartType;
  metric: SentimentMetricValues;
  primaryFilterSelections?: TreeSelection[];
}): SentimentData => {
  const metricKey = (
    metric === 'sentiment_top_bottom' ? 'overall' : metric
  ) as SentimentMetricKeys;

  const primarySortedData = primaryFilterSelections
    ? [...data].sort((a, b) => {
        const aIndex = primaryFilterSelections.findIndex((selection) => {
          const splitIds = selection.id.split('_');
          const lastId = splitIds[splitIds.length - 1];
          return lastId === `${a?.metadata?.id}`;
        });
        const bIndex = primaryFilterSelections.findIndex((selection) => {
          const splitIds = selection.id.split('_');
          const lastId = splitIds[splitIds.length - 1];
          return lastId === `${b?.metadata?.id}`;
        });
        return aIndex - bIndex;
      })
    : data;

  if (
    chartType === 'diverging-bar-combo' &&
    metric === 'sentiment_top_bottom'
  ) {
    const mapped: BreakdownSingleDataSet['data'] = primarySortedData
      .map((entity) => {
        const dim = entity?.metadata?.shortName;
        if (!dim || !entity) return null;

        const breakdown = toTopBottomBreakdown(entity);
        if (breakdown.length === 0) return null;

        return {
          ...(entity?.metadata?.metadata_type === 'DATA_KIND_COMPANY' &&
          entity?.metadata?.id
            ? { id: String(entity?.metadata?.id) }
            : {}),
          dim,
          breakdown,
        };
      })
      .filter((point) => point !== null);

    return { type: DataType.BREAKDOWN_SINGLE, data: mapped };
  }

  if (chartType === 'line') {
    const mapped: TimeseriesDataSet['data'] = primarySortedData
      .map((entity) => {
        const dateRefs = entity?.months_refs;

        const metadata = entity?.metadata;
        const dim = metadata?.shortName;
        const metricScores = entity?.result?.scores?.[metricKey];

        if (!dim || !metricScores || !dateRefs) return null;

        const timeseries = metricScores
          .map((metricDataPoint) => {
            const monthId = metricDataPoint?.month_id;
            if (!monthId) return null;
            const date = dateRefs[monthId]?.shortName;
            if (!date) return null;
            return { date, value: (metricDataPoint?.value ?? 0) / 100 };
          })
          .filter((point) => point !== null);

        return {
          ...(entity?.metadata?.metadata_type === 'DATA_KIND_COMPANY' &&
          entity?.metadata?.id
            ? { id: String(entity?.metadata?.id) }
            : {}),
          dim,
          timeseries,
        };
      })
      .filter((point) => point !== null);

    return { type: DataType.TIMESERIES, data: mapped };
  }

  const mapped: SingleDataSet['data'] = primarySortedData
    .map((entity) => {
      const dim = entity?.metadata?.shortName;
      const metricScores = entity?.result?.scores?.[metricKey];
      const lastMetricScore = metricScores?.[metricScores.length - 1];
      const value = (lastMetricScore?.value ?? 0) / 100;
      if (!dim) return null;
      return {
        ...(entity?.metadata?.metadata_type === 'DATA_KIND_COMPANY' &&
        entity?.metadata?.id
          ? { id: String(entity?.metadata?.id) }
          : {}),
        dim,
        value,
      };
    })
    .filter((point) => point !== null);

  return { type: DataType.SINGLE, data: mapped };
};
